import { InfoSet } from "../InfoSets/InfoSet";
import { MacAddress } from "../Core/MacAddress";
import { IpAddress } from "../Core/IpAddress";
import { Device } from "../Core/Device";
import { HashSet } from "../Utils/HashSet";
import { List } from "../Utils/List";
import { Iterable } from "../Utils/ObjUtils";
import { IpAddressTable, IpAddressEntry } from "../InfoSets/IpAddressTable";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class VrrpTable extends InfoSet implements Iterable<VrrpEntry>
{
	private _vrrpList: List<VrrpEntry>;

	constructor(device: Device) {
		super(device);

		this._vrrpList = new List<VrrpEntry>();
	}

	public Add(ifIndex: number, vrId: number): VrrpEntry {
		let vrrpEntry: VrrpEntry = null;

		vrrpEntry = this.Find(ifIndex, vrId);
		if (vrrpEntry == null) {
			vrrpEntry = new VrrpEntry(ifIndex, vrId);
			this._vrrpList.Add(vrrpEntry);		
		}

		return vrrpEntry;
	}

	public AddEntry(vrrpEntry: VrrpEntry): boolean {
		if (vrrpEntry == null)
			return false;

		let foundEntry: VrrpEntry = this.Find(vrrpEntry.IfIndex, vrrpEntry.VirtualRouterId);
		if (foundEntry == null) {
			this._vrrpList.Add(vrrpEntry);
			return true;
		}

		return false;
	}

	public Clear(): void {
		this._vrrpList.Clear();
	}

	public get Count(): number {
		return this._vrrpList.Count;
	}

	public Find(ifIndex: number, vrId: number): VrrpEntry {

		let foundEntry: VrrpEntry = null;

		for (let i = 0; i < this._vrrpList.Count; ++i) {
			let vrrpEntry: VrrpEntry = this._vrrpList.Get(i);

			if (vrrpEntry.IfIndex != ifIndex)
				continue;
			if (vrrpEntry.VirtualRouterId != vrId)
				continue;

			foundEntry = vrrpEntry;
			break;
		}

		return foundEntry;
	}

	public Remove(ifIndex: number, vrId: number): void {
		let foundEntry: VrrpEntry = this.Find(ifIndex, vrId);
		if (foundEntry != null) {
			this._vrrpList.Remove(foundEntry);
		}
	}

	public get HasIpAddresses(): boolean {
		return true;
	}

	public get IpAddresses(): HashSet<IpAddress> {

		let ipAddresses = new HashSet<IpAddress>();

		this._vrrpList.ForEach((vrrpEntry: VrrpEntry) => {

			// CHECK to see if this entry represents the ACTIVE ROUTER configuration
			if (!vrrpEntry.IsActiveRouter)
				return;

			// CHECK to ensure that we know which device is MASTER (by IP ADDRESS)
			if (!vrrpEntry.HasMasterIpAddress)
				return;

			// VERIFY that the Master IP ADDRESS is contained by this device (the ACTIVE ROUTER)
			if (this.Device.HasIpAddressTable) {
				if (this.Device.IpAddressTable.ContainsIpAddress(vrrpEntry.MasterIpAddress)) {
					// REGISTER the Primary IP ADDRESS with the 
					if (vrrpEntry.HasPrimaryIpAddress)
						ipAddresses.Add(vrrpEntry.PrimaryIpAddress);

					if (vrrpEntry.HasAssociatedIpAddresses) {
						vrrpEntry.AssociatedIpAddresses.ForEach((ipEntry: IpAddressEntry) => {
							if (ipEntry.IpAddress)
								ipAddresses.Add(ipEntry.IpAddress);
						}, this);
					}
				}
			}
		}, this);

		return (ipAddresses);
	}

	public get HasIpSightings(): boolean {
		return true;
	}

	public get IpSightings(): HashSet<IpAddress> {

		let ipAddresses = new HashSet<IpAddress>();

		this._vrrpList.ForEach((vrrpEntry: VrrpEntry) => {

			if (vrrpEntry.HasMasterIpAddress)
				ipAddresses.Add(vrrpEntry.MasterIpAddress);

			if (vrrpEntry.HasPrimaryIpAddress)
				ipAddresses.Add(vrrpEntry.PrimaryIpAddress);

			if (vrrpEntry.HasAssociatedIpAddresses) {
				vrrpEntry.AssociatedIpAddresses.ForEach((ipEntry: IpAddressEntry) => {
					if (ipEntry.IpAddress && ipEntry.IpAddress.IsValidDiscoveryAddress)
						ipAddresses.Add(ipEntry.IpAddress);
				}, this);
			}
		}, this);

		return (ipAddresses);
	}

	public ForEach(callbackfn: (value: VrrpEntry, index: number, array: VrrpEntry[]) => void, thisArg?: any): void {
		this._vrrpList.ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: VrrpEntry, index: number, array: VrrpEntry[]) => boolean, thisArg?: any): boolean {
		return this._vrrpList.Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: VrrpEntry, index: number, array: VrrpEntry[]) => boolean, thisArg?: any): boolean {
		return this._vrrpList.Every(callbackfn, thisArg);
	}

	public ToArray(): VrrpEntry[] {
		return this._vrrpList.ToArray();
	}

	public Write(obj: any, context: any): void {
		if (this._vrrpList.Count > 0) {
			obj.vrrp_info = {};
			obj = obj.vrrp_info;

			obj.entries = [];
			ObjSerUtils.WriteObjSerCollection(obj.entries, this._vrrpList, context);
		}
	}

	public Read(obj: any, context: any): void {

		this.Clear();

		if (!obj.vrrp_info)
			throw new Error();
		obj = obj.vrrp_info;

		if (obj.entries !== undefined) {
			ObjSerUtils.ReadObjSerCollection(obj.entries, this._vrrpList, (value: any) => {
				return new VrrpEntry();
			}, context);
		}
	}

}


export class VrrpEntry {

	public static readonly vrrpOperState_initialize = 1;
	public static readonly vrrpOperState_backup = 2;
	public static readonly vrrpOperState_master = 3;

	public static readonly vrrpAdminState_up = 1;
	public static readonly vrrpAdminState_down = 2;

	// KEY
	public IfIndex: number;
	public VirtualRouterId: number;

	public VirtualMacAddress: MacAddress;
	public get HasVirtualMacAddress(): boolean {
		if (this.VirtualMacAddress != null) {
			if (!this.VirtualMacAddress.IsNull)
				return true;
		}
		return false;
	}

	public OperState: number;
	public get HasOperState(): boolean {
		if (this.OperState >= 0)
			return true;
		return false;
	}

	public get IsActiveRouter(): boolean {
		if (this.AdminState == VrrpEntry.vrrpAdminState_up) {
			if (this.OperState == VrrpEntry.vrrpOperState_master) {
				return true;
			}
		}
		return false;
	}

	public get IsStandbyRouter(): boolean {
		if (this.AdminState == VrrpEntry.vrrpAdminState_up) {
			if (this.OperState == VrrpEntry.vrrpOperState_backup)
				return true;
		}
		return true;
	}

	public AdminState: number;
	public get HasAdminState(): boolean {
		if (this.AdminState > 0)
			return true;
		return false;
	}

	public IpAddressCount: number;
	public get HasIpAddressCount(): boolean {
		if (this.IpAddressCount >= 0)
			return true;
		return false;
	}

	public AssociatedIpAddresses: IpAddressTable;
	public get HasAssociatedIpAddresses(): boolean {
		if (this.AssociatedIpAddresses == null)
			return false;
		if (this.AssociatedIpAddresses.Count <= 0)
			return false;
		return true;
	}

	public MasterIpAddress: IpAddress;
	public get HasMasterIpAddress(): boolean {
		if (this.MasterIpAddress != null)
			return true;
		return false;
	}

	public PrimaryIpAddress: IpAddress;
	public get HasPrimaryIpAddress(): boolean {
		if (this.PrimaryIpAddress != null)
			return true;
		return false;
	}

	public constructor(ifIndex?: number, vrId?: number) {
		this._Init();

		if (ifIndex !== undefined) {
			this.IfIndex = ifIndex;
		}
		if (vrId !== undefined) {
			this.VirtualRouterId = vrId;
		}
	}

	private _Init(): void {
		this.IfIndex = -1;
		this.VirtualRouterId = -1;

		this.VirtualMacAddress = null;
		this.OperState = -1;
		this.AdminState = 0;
		this.IpAddressCount = -1;
		this.AssociatedIpAddresses = new IpAddressTable(null);
		this.MasterIpAddress = null;
		this.PrimaryIpAddress = null;
	}

	public Write(obj: any, context: any): void {
		obj.vrrp_entry = {};
		obj = obj.vrrp_entry;

		obj.if_index = ValueEncoder.EncodeInteger(this.IfIndex);
		obj.virtual_router_id = ValueEncoder.EncodeInteger(this.VirtualRouterId);

		if (this.HasVirtualMacAddress)
			obj.virtual_mac_address = ValueEncoder.EncodeMacAddress(this.VirtualMacAddress);

		if (this.HasOperState)
			obj.oper_state = ValueEncoder.EncodeInteger(this.OperState);

		if (this.AdminState)
			obj.admin_state = ValueEncoder.EncodeInteger(this.AdminState);

		if (this.HasIpAddressCount)
			obj.ip_count = ValueEncoder.EncodeInteger(this.IpAddressCount);

		if (this.HasAssociatedIpAddresses)
			this.AssociatedIpAddresses.Write(obj, context);

		if (this.HasMasterIpAddress)
			obj.master_ip = ValueEncoder.EncodeIpAddress(this.MasterIpAddress);

		if (this.HasPrimaryIpAddress)
			obj.primary_ip = ValueEncoder.EncodeIpAddress(this.PrimaryIpAddress);
	}

	public Read(obj: any, context: any): void {
		if (!obj.vrrp_entry)
			throw new Error();
		obj = obj.vrrp_entry;

		if (obj.if_index !== undefined)
			this.IfIndex = ValueEncoder.DecodeInteger(obj.if_index);
		if (obj.virtual_router_id !== undefined)
			this.VirtualRouterId = ValueEncoder.DecodeInteger(obj.virtual_router_id);
		if (obj.virtual_mac_address !== undefined)
			this.VirtualMacAddress = ValueEncoder.DecodeMacAddress(obj.virtual_mac_address);
		if (obj.oper_state !== undefined)
			this.OperState = ValueEncoder.DecodeInteger(obj.oper_state);
		if (obj.admin_state !== undefined)
			this.AdminState = ValueEncoder.DecodeInteger(obj.admin_state);
		if (obj.ip_count !== undefined)
			this.IpAddressCount = ValueEncoder.DecodeInteger(obj.ip_count);
		if (obj.ip_info !== undefined)
			this.AssociatedIpAddresses.Read(obj, context);
		if (obj.master_ip !== undefined)
			this.MasterIpAddress = ValueEncoder.DecodeIpAddress(obj.master_ip);
		if (obj.primary_ip !== undefined)
			this.PrimaryIpAddress = ValueEncoder.DecodeIpAddress(obj.primary_ip);
	}

}